/**
 * CREATIVE DETONATOR - Timing Utilities
 * Debounce, throttle, and rate limiting for performance optimization
 *
 * @version 1.0.0
 * @description Implements FRONTEND-28 (Input Debouncing)
 */

// ============================================================================
// DEBOUNCE (Trailing Edge by Default)
// ============================================================================

/**
 * Debounce function - delays execution until calls stop
 *
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Delay in ms (default: 300)
 * @param {Object} options - Configuration options
 * @param {boolean} options.leading - Execute on leading edge (default: false)
 * @param {boolean} options.trailing - Execute on trailing edge (default: true)
 * @returns {Function} Debounced function with cancel() and flush() methods
 *
 * @example
 * const debouncedSave = debounce(save, 500);
 * input.addEventListener('input', debouncedSave);
 *
 * // Later: debouncedSave.cancel() or debouncedSave.flush()
 */
export function debounce(fn, delay = 300, options = {}) {
  const { leading = false, trailing = true } = options;

  let timeoutId = null;
  let lastArgs = null;
  let lastThis = null;
  let calledLeading = false;

  function debounced(...args) {
    lastArgs = args;
    lastThis = this;

    // Clear existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Leading edge execution (first call only)
    if (leading && !timeoutId && !calledLeading) {
      calledLeading = true;
      fn.apply(this, args);
    }

    // Set trailing timeout
    timeoutId = setTimeout(() => {
      timeoutId = null;
      calledLeading = false;
      if (trailing && lastArgs) {
        fn.apply(lastThis, lastArgs);
      }
      lastArgs = null;
      lastThis = null;
    }, delay);
  }

  // Cancel pending execution
  debounced.cancel = function() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    lastArgs = null;
    lastThis = null;
    calledLeading = false;
  };

  // Flush: execute immediately if pending
  debounced.flush = function() {
    if (timeoutId && lastArgs) {
      clearTimeout(timeoutId);
      timeoutId = null;
      fn.apply(lastThis, lastArgs);
      lastArgs = null;
      lastThis = null;
    }
  };

  // Check if there's a pending call
  debounced.pending = function() {
    return timeoutId !== null;
  };

  return debounced;
}

// ============================================================================
// THROTTLE
// ============================================================================

/**
 * Throttle function - limits execution rate
 *
 * @param {Function} fn - Function to throttle
 * @param {number} limit - Minimum time between calls in ms (default: 100)
 * @param {Object} options - Configuration options
 * @param {boolean} options.leading - Execute on leading edge (default: true)
 * @param {boolean} options.trailing - Execute on trailing edge (default: true)
 * @returns {Function} Throttled function with cancel() method
 *
 * @example
 * const throttledScroll = throttle(handleScroll, 100);
 * window.addEventListener('scroll', throttledScroll);
 */
export function throttle(fn, limit = 100, options = {}) {
  const { leading = true, trailing = true } = options;

  let lastRun = 0;
  let timeoutId = null;
  let lastArgs = null;
  let lastThis = null;

  function throttled(...args) {
    const now = Date.now();
    const elapsed = now - lastRun;

    lastArgs = args;
    lastThis = this;

    if (elapsed >= limit) {
      // Clear any pending trailing call
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (leading || lastRun !== 0) {
        lastRun = now;
        fn.apply(this, args);
      } else {
        lastRun = now;
      }
    } else if (trailing && !timeoutId) {
      // Schedule trailing call
      timeoutId = setTimeout(() => {
        lastRun = Date.now();
        timeoutId = null;
        fn.apply(lastThis, lastArgs);
      }, limit - elapsed);
    }
  }

  throttled.cancel = function() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    lastArgs = null;
    lastThis = null;
  };

  return throttled;
}

// ============================================================================
// ASYNC DEBOUNCE (With AbortController for fetch cancellation)
// ============================================================================

/**
 * Debounce async function with automatic request cancellation
 * Perfect for search inputs that trigger API calls
 *
 * @param {Function} fn - Async function to debounce (receives { signal } as last arg)
 * @param {number} delay - Delay in ms (default: 300)
 * @returns {Function} Debounced async function
 *
 * @example
 * const debouncedSearch = debounceAsync(async (query, { signal }) => {
 *   const res = await fetch(`/api/search?q=${query}`, { signal });
 *   return res.json();
 * }, 300);
 *
 * input.addEventListener('input', async (e) => {
 *   try {
 *     const results = await debouncedSearch(e.target.value);
 *     displayResults(results);
 *   } catch (err) {
 *     if (err.name !== 'AbortError') throw err;
 *   }
 * });
 */
export function debounceAsync(fn, delay = 300) {
  let timeoutId = null;
  let abortController = null;

  async function debounced(...args) {
    // Cancel previous request
    if (abortController) {
      abortController.abort();
    }

    // Clear previous timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Create new abort controller
    abortController = new AbortController();
    const signal = abortController.signal;

    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(async () => {
        try {
          const result = await fn.apply(this, [...args, { signal }]);
          if (!signal.aborted) {
            resolve(result);
          }
        } catch (error) {
          if (!signal.aborted) {
            reject(error);
          }
        } finally {
          timeoutId = null;
        }
      }, delay);
    });
  }

  debounced.cancel = function() {
    if (timeoutId) clearTimeout(timeoutId);
    if (abortController) abortController.abort();
    timeoutId = null;
    abortController = null;
  };

  return debounced;
}

// ============================================================================
// RATE LIMITER
// ============================================================================

/**
 * Rate limiter - allows N calls per time window
 *
 * @param {Function} fn - Function to rate limit
 * @param {number} maxCalls - Maximum calls allowed
 * @param {number} window - Time window in ms (default: 1000)
 * @returns {Function} Rate-limited function
 *
 * @example
 * const limitedApi = rateLimit(apiCall, 5, 1000); // 5 calls per second
 */
export function rateLimit(fn, maxCalls, window = 1000) {
  const calls = [];

  return function(...args) {
    const now = Date.now();

    // Remove calls outside the window
    while (calls.length && calls[0] <= now - window) {
      calls.shift();
    }

    if (calls.length < maxCalls) {
      calls.push(now);
      return fn.apply(this, args);
    }

    console.warn('[RateLimit] Call dropped - rate limit exceeded');
    return undefined;
  };
}

// ============================================================================
// REQUEST ANIMATION FRAME THROTTLE
// ============================================================================

/**
 * Throttle to animation frame rate (for DOM operations)
 * Ensures function runs at most once per frame (~16ms at 60fps)
 *
 * @param {Function} fn - Function to throttle
 * @returns {Function} RAF-throttled function
 */
export function rafThrottle(fn) {
  let rafId = null;
  let lastArgs = null;

  function throttled(...args) {
    lastArgs = args;

    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        fn.apply(this, lastArgs);
        rafId = null;
      });
    }
  }

  throttled.cancel = function() {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  return throttled;
}

// ============================================================================
// DELAY / SLEEP
// ============================================================================

/**
 * Promise-based delay
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise} Resolves after delay
 */
export function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Delay with cancellation support
 * @param {number} ms - Milliseconds to wait
 * @returns {{ promise: Promise, cancel: Function }}
 */
export function cancellableDelay(ms) {
  let timeoutId;
  let rejectFn;

  const promise = new Promise((resolve, reject) => {
    rejectFn = reject;
    timeoutId = setTimeout(resolve, ms);
  });

  return {
    promise,
    cancel: () => {
      clearTimeout(timeoutId);
      rejectFn(new Error('Cancelled'));
    }
  };
}

// ============================================================================
// GLOBAL EXPORT (for non-module usage)
// ============================================================================

window.Timing = {
  debounce,
  throttle,
  debounceAsync,
  rateLimit,
  rafThrottle,
  delay,
  cancellableDelay
};

export default {
  debounce,
  throttle,
  debounceAsync,
  rateLimit,
  rafThrottle,
  delay,
  cancellableDelay
};
